import React, { useState } from "react";
import { Link } from "react-router-dom";
import useInView from "../hooks/useInView";
import { Helmet } from "react-helmet-async";

export default function Home() {
  const gallery = [
    {
      img: "/img/hero.png",
      title: "Fully Custom Salon Experience",
      desc: "We don’t use templates. Every salon system is designed to reflect your identity while powered by a strong backend engine."
    },
    {
      img: "/img/hero3.png",
      title: "Smart Booking & Chatbot System",
      desc: "Automated appointment scheduling with integrated chatbot that helps clients book instantly and improves your workflow."
    },
    {
      img: "/img/hero2.png",
      title: "Complete Salon Dashboard Control",
      desc: "Manage your clients, appointments, and daily operations through a powerful and easy-to-use dashboard built for salon owners."
    }
  ];

  const [featuresRef, featuresVisible] = useInView();
  const [ctaRef, ctaVisible] = useInView();

  const faqs = [
    {
      question: "What makes Maniera salon software different?",
      answer: "ManieraDigital builds fully custom salon systems. Each salon gets a unique design while using a powerful shared engine with booking, chatbot, and dashboard features."
    },
    {
      question: "Does the system include appointment scheduling?",
      answer: "Yes, the platform includes a smart booking system with real-time scheduling and chatbot integration to automate appointments."
    },
    {
      question: "Can I manage clients and operations?",
      answer: "Absolutely. You get a full dashboard where you can track clients, manage bookings, and control your salon workflow efficiently."
    },
    {
      question: "Do you provide hosting and maintenance?",
      answer: "Yes, we handle everything — hosting, system performance, and updates — so you can focus on your salon."
    },
    {
      question: "Is the system SEO optimized?",
      answer: "Yes, every salon platform is optimized for search engines to help you rank on Google and attract more clients organically."
    }
  ];

  const [activeFAQ, setActiveFAQ] = useState(null);
  const toggleFAQ = (index) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Custom Salon Software & Booking System | Maniera</title>
        <meta
          name="description"
          content="Maniera provides custom salon software with booking system, appointment scheduling, and SEO optimization. Grow your salon online."
        />
        <meta
          name="keywords"
          content="salon software, booking system for salons, salon management system, appointment scheduling software, Maniera SaaS"
        />
        <link rel="canonical" href="https://www.manieradigital.com/" />

        <meta property="og:title" content="Custom Salon Software | Maniera" />
        <meta
          property="og:description"
          content="Boost your salon with custom booking system and SEO-optimized platform."
        />
        <meta property="og:url" content="https://www.manieradigital.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/img/hero.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Salon Software | Maniera" />
        <meta
          name="twitter:description"
          content="Custom salon booking system and software for growth."
        />
        <meta name="twitter:image" content="/img/hero.png" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Maniera",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description:
              "Custom salon software with booking system, appointment scheduling, and SEO tools.",
            url: "https://www.manieradigital.com/",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "EUR"
            }
          })}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="fade-in">
              Custom Salon Software with AI Receptionist & Smart Booking System
          </h1>
          <p className="fade-in delay-1">
            ManieraDigital builds fully custom salon software that gives your brand a unique identity. 
            From booking systems and smart scheduling to client dashboards and chatbot automation — 
            everything is built for growth, visibility, and full control of your salon business.
          </p>
          <div className="hero-buttons fade-in delay-2">
            <Link to="/services" className="hero-button primary">
              Explore Services
            </Link>
            <Link to="/salon-software-demo" className="hero-button secondary">
              Explore Our Demo
            </Link>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="gallery-section">
        <div className="gallery-collage">
          {gallery.map((item, idx) => (
            <div className="gallery-card fade-in" key={idx}>
              <div
                className="gallery-image"
                style={{ backgroundImage: `url(${item.img})` }}
              >
                <img
                  src={item.img}
                  loading="lazy"
                  alt={`${item.title} - salon booking system interface`}
                  style={{ display: "none" }}
                />
              </div>
              <div className="gallery-text">
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section
        ref={featuresRef}
        className={`features-section fade-up ${featuresVisible ? "show" : ""}`}
      >
        <h2>Why Choose Maniera Salon Software</h2>
        <div className="features-container">
          <div className="feature-card">
            <h3>Fully Custom Salon Software</h3>
            <p>
              Every salon gets a unique digital identity. We build custom interfaces tailored to your brand while keeping a powerful shared engine behind the system.
            </p>
          </div>
          <div className="feature-card">
            <h3>Advanced Booking & Scheduling</h3>
            <p>
              Smart booking system with real-time scheduling, chatbot integration, and automated appointment handling to simplify your daily operations.
            </p>
          </div>
          <div className="feature-card">
            <h3>Client Dashboard & Full Control</h3>
            <p>
              Track clients, manage appointments, and control your entire salon workflow through an intuitive dashboard built for efficiency and growth.
            </p>
          </div>
          <div className="feature-card">
            <h3>SEO Optimization & Hosting</h3>
            <p>
              We handle everything — from hosting to SEO optimization — ensuring your salon ranks on Google and attracts new clients organically.
            </p>
          </div>
        </div>
       <div className="features-seo">
  <p className="features-seo-text">
    ManieraDigital provides a complete salon software solution — from design and development to deployment, hosting, and SEO positioning. 
    Learn more about our <Link to="/services">salon software services</Link>.
  </p>
</div>
      </section>

      {/* SEO TEXT BLOCK */}
 {/* SEO TEXT BLOCK */}
<section className="seo-section">
  <div className="seo-container">
    <div className="seo-left">
      <span className="seo-subtitle">Premium Experience</span>
      <h2>Built for Modern Salons</h2>
      <p>
        ManieraDigital creates custom digital systems designed to reflect your brand — not templates. 
        We focus on high-end aesthetics that convert visitors into loyal clients.
      </p>
      {/* Dodatni vizuelni element za popunjavanje praznine */}
      <div className="seo-badges">
        <div className="badge">✓ Custom Design</div>
        <div className="badge">✓ SEO Ready</div>
        <div className="badge">✓ 24/7 Support</div>
      </div>
    </div>

    <div className="seo-right">
      <div className="seo-card">
        <p>
          Each platform runs on a <strong>powerful engine</strong> with booking, chatbot automation, and smart scheduling.
        </p>
        <p>
          Manage clients and appointments through a clean dashboard — while we handle hosting and SEO optimization.
        </p>
        <div className="seo-divider"></div>
        <p className="seo-highlight">
          Your salon doesn’t just look premium — it performs, ranks, and grows.
        </p>
      </div>
    </div>
  </div>
</section>

      {/* INSTAGRAM */}
      <section className="instagram-section fade-up">
        <h2>Salon Software in Action</h2>
        <div className="instagram-feed">
          <a href="https://www.instagram.com/manieradigital/" target="_blank" rel="noopener noreferrer">
            <img src="/img/insta1.jpg" loading="lazy" alt="Salon booking system dashboard Maniera" />
          </a>
          <a href="https://www.instagram.com/manieradigital/" target="_blank" rel="noopener noreferrer">
            <img src="/img/insta2.jpg" loading="lazy" alt="Salon management software interface" />
          </a>
          <a href="https://www.instagram.com/manieradigital/" target="_blank" rel="noopener noreferrer">
            <img src="/img/insta3.jpg" loading="lazy" alt="Appointment scheduling software UI" />
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="faq-container">
          <h2>Frequently Asked Questions</h2>
          {faqs.map((item, idx) => (
            <div
              key={idx}
              className={`faq-item ${activeFAQ === idx ? "active" : ""}`}
              onClick={() => toggleFAQ(idx)}
            >
              <div className="faq-question">
                <h3>{item.question}</h3>
                <span>+</span>
              </div>
              <div className="faq-answer">
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        ref={ctaRef}
        className={`cta-section fade-up ${ctaVisible ? "show" : ""}`}
      >
        <h2>Build Your Salon System with Maniera</h2>
        <p>
          Get a fully custom salon software solution with booking system, client dashboard, and SEO optimization — all in one place.
        </p>
        <Link to="/contact" className="cta-button">
          Get Started
        </Link>
      </section>
    </>
  );
}


